import { ApiError } from '@commissionsight/sdk';

/**
 * Registration and OTP login are internal web-app flows, NOT part of the public
 * SDK surface, so the app calls these endpoints directly. (The SDK is for
 * external integrators working against an already-provisioned account token.)
 */

async function post<T>(baseUrl: string, path: string, body: unknown): Promise<T> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  const parsed = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message =
      parsed && typeof parsed === 'object' && 'title' in parsed
        ? String((parsed as { title: unknown }).title)
        : res.statusText;
    throw new ApiError(res.status, message, parsed);
  }
  return parsed as T;
}

/** Create a pending account. The registrant must agree to the Terms/Privacy. */
export function register(
  baseUrl: string,
  email: string,
  accountName: string,
  consent: { acceptedTerms: boolean; termsVersion?: string },
  // Invisible anti-bot signals: honeypot (stays empty for humans) + the ms epoch
  // when the form rendered (a too-fast submit is rejected server-side).
  bot: { website?: string; formStartedAt?: number } = {},
  referralCode?: string,
): Promise<{ accountId: string }> {
  return post(baseUrl, '/auth/register', {
    email,
    accountName,
    acceptedTerms: consent.acceptedTerms,
    ...(consent.termsVersion ? { termsVersion: consent.termsVersion } : {}),
    website: bot.website ?? '',
    ...(bot.formStartedAt ? { formStartedAt: bot.formStartedAt } : {}),
    ...(referralCode ? { referralCode } : {}),
  });
}

/** Request a one-time login code be emailed to a known/allowlisted address. */
export function requestOtp(baseUrl: string, email: string): Promise<{ status: string }> {
  return post(baseUrl, '/auth/otp/request', { email });
}

/** Verify a one-time code → session token + account/role. */
export function verifyOtp(
  baseUrl: string,
  email: string,
  code: string,
): Promise<{
  token: string;
  account: { accountId: string; status: string } | null;
  role: string;
}> {
  return post(baseUrl, '/auth/otp/verify', { email, code });
}

export interface AuthProviders {
  google: boolean;
  microsoft: boolean;
}

/** Which OAuth providers the API has configured (used to show/hide buttons). */
export async function getAuthProviders(baseUrl: string): Promise<AuthProviders> {
  try {
    const res = await fetch(`${baseUrl.replace(/\/$/, '')}/auth/providers`);
    if (!res.ok) return { google: false, microsoft: false };
    return (await res.json()) as AuthProviders;
  } catch {
    return { google: false, microsoft: false };
  }
}

/** Full-page URL that kicks off the OAuth flow for a provider. Carries the
 *  referral code (if any) so signups via "Continue with Google" stay attributed. */
export function oauthStartUrl(
  baseUrl: string,
  provider: 'google' | 'microsoft',
  referralCode?: string,
): string {
  const base = `${baseUrl.replace(/\/$/, '')}/auth/oauth/${provider}/start`;
  return referralCode ? `${base}?ref=${encodeURIComponent(referralCode)}` : base;
}

/** Finish a new OAuth user's signup (verified email + account name + consent). */
export function completeOauth(
  baseUrl: string,
  body: { grant: string; accountName: string; acceptedTerms: boolean; termsVersion?: string },
): Promise<{
  token: string;
  account: { accountId: string; status: string } | null;
  role: string;
}> {
  return post(baseUrl, '/auth/oauth/complete', body);
}
